import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ComponentErrorBoundary from 'core/modules/ErrorBoundaries/ComponentErrorBoundary';
import isTouchDevice from 'core/utils/helpers/isTouchDevice';

import './Tooltip.scss';

function Tooltip({
  direction, content, customClassName, shouldShowCloseButton, useDefaultInfoIcon, children,
  shouldShowOnHover,
}) {
  const [shouldAddTooltipHint, setShouldAddTooltipHint] = useState(false);
  const tooltipHintRef = useRef();
  const tooltipWrapperRef = useRef();

  const addTooltipHint = () => setShouldAddTooltipHint(true);
  const removeTooltipHint = () => setShouldAddTooltipHint(false);
  const toggleTooltipHint = () => setShouldAddTooltipHint(!shouldAddTooltipHint);

  // Adjust tooltip hint position if it overlaps viewport
  const adjustAndShowHint = (tooltipHintElement, clientWidth) => {
    const tooltipHintPosition = tooltipHintElement.getBoundingClientRect();
    const topOrBottomTooltipPosition = direction === 'top' || direction === 'bottom';

    if (topOrBottomTooltipPosition && tooltipHintPosition.right > clientWidth) {
      const rightOverlap = tooltipHintPosition.right - clientWidth;
      const initialTooltipHintLeft = window.getComputedStyle(tooltipHintElement).left;
      tooltipHintElement.style.left = `calc(${initialTooltipHintLeft} - ${rightOverlap}px - 10px)`;
    }

    if (topOrBottomTooltipPosition && tooltipHintPosition.left < 0) {
      const leftOverlap = tooltipHintPosition.left;
      const initialTooltipHintLeft = window.getComputedStyle(tooltipHintElement).left;
      tooltipHintElement.style.left = `calc(${initialTooltipHintLeft} - ${leftOverlap}px + 10px)`;
    }

    tooltipHintElement.style.visibility = 'visible';
  };

  const handleOnMouseEnter = (event) => ((isTouchDevice || !shouldShowOnHover) ? null : addTooltipHint(event));
  const handleOnMouseLeave = (event) => ((isTouchDevice || !shouldShowOnHover) ? null : removeTooltipHint(event));
  const handleOnFocus = (event) => (isTouchDevice ? null : addTooltipHint(event));
  const handleOnBlur = (event) => (isTouchDevice ? null : removeTooltipHint(event));

  useEffect(() => {
    const tooltipHintElement = tooltipHintRef.current;

    if (shouldAddTooltipHint && tooltipHintElement) {
      adjustAndShowHint(tooltipHintElement, document.body.clientWidth);
    }
  }, [shouldAddTooltipHint]);

  const handleClickOutside = (event) => {
    if (!tooltipWrapperRef.current.contains(event.target)) {
      setTimeout(() => removeTooltipHint(), 100);
    }
  };

  useEffect(() => {
    // Track mousedown event only if tooltip ref is passed
    if (tooltipWrapperRef?.current) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  });

  const renderTooltipHint = () => (
    <div ref={tooltipHintRef} className={`mn_tooltipHint mn_${direction}`}>
      { shouldShowCloseButton &&
        <button className="mn_closeButton" onClick={removeTooltipHint} type="button" aria-label="Close tip" />
      }
      {content}
    </div>
  );

  return (
    <div className={`mn_tooltipWrapper ${customClassName}`} ref={tooltipWrapperRef}>
      <button
        className={`mn_tooltipButton ${useDefaultInfoIcon ? 'mn_tooltipInfoIcon' : ''}`}
        type="button"
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onClick={toggleTooltipHint}
        aria-label="Tooltip"
      >
        {children}
      </button>
      { shouldAddTooltipHint && renderTooltipHint() }
    </div>
  );
}

Tooltip.propTypes = {
  direction: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  content: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  customClassName: PropTypes.string,
  useDefaultInfoIcon: PropTypes.bool,
  shouldShowOnHover: PropTypes.bool,
};

Tooltip.defaultProps = {
  direction: 'top',
  customClassName: '',
  content: null,
  children: null,
  useDefaultInfoIcon: false,
  shouldShowOnHover: true,
};

export default (props) => <ComponentErrorBoundary><Tooltip {...props} /></ComponentErrorBoundary>;
